import { UseFormRegisterReturn } from 'react-hook-form'
import { twMerge } from 'tailwind-merge'
import { AlertErrorCircle } from '@/assets/icons'

interface FloatLabelFormTextareaProps {
  label: string
  id: string
  required?: boolean
  error?: string
  disabled?: boolean
  registration: UseFormRegisterReturn
  rows?: number
  textareaProps?: React.TextareaHTMLAttributes<HTMLTextAreaElement>
  /** Optional classes on the outer field wrapper (e.g. flex-1 to fill modal space). */
  className?: string
}

/**
 * Form textarea with a pinned top label inside a bordered group.
 *
 * Unlike InputFieldFloatingLabel, the label is not absolutely positioned over the
 * control. Native textarea scroll text can paint through overlays and borders;
 * keeping the label in a non-scrolling band above the textarea avoids that.
 */
export default function FloatLabelFormTextarea(props: FloatLabelFormTextareaProps) {
  const { label, id, required, error, disabled, registration, rows = 5, className, textareaProps } = props
  return (
    <div className={twMerge('flex flex-col gap-1', className)}>
      <div
        className={twMerge(
          'flex min-h-0 flex-1 flex-col overflow-hidden rounded border bg-surface-field',
          error ? 'border-border-field-error' : 'border-border-field focus-within:border-border-field-active',
          disabled && 'bg-surface-subtle'
        )}
      >
        <label htmlFor={id} className="shrink-0 px-3 pb-0.5 pt-1.5 text-xs text-text-tertiary">
          {label}
          {required && <span className="ml-[2px] text-red-700">*</span>}
        </label>
        <textarea
          className="min-h-0 w-full flex-1 resize-none border-0 bg-transparent px-3 pb-1.5 pt-0 focus:outline-hidden focus:ring-0 overscroll-contain disabled:cursor-not-allowed disabled:text-brand-neutral-700"
          id={id}
          rows={rows}
          aria-invalid={!!error}
          aria-required={required}
          {...registration}
          {...textareaProps}
          disabled={disabled}
        />
      </div>
      {error && (
        <p className="mt-1 flex items-center gap-1 rounded bg-red-50 p-2 text-xs text-red-800">
          <AlertErrorCircle className="h-3.5 w-3.5 shrink-0" />
          {error}
        </p>
      )}
    </div>
  )
}
